import React, {useEffect, useMemo, useState} from 'react';
import AppHeader from '../components/AppHeader.js';
import post from '../utils/post.js';
import '../styles/instructorHome.scss';

function formatDayLabel(value) {
	if (!value) {
		return '';
	}

	return new Intl.DateTimeFormat('cs-CZ', {
		weekday: 'long',
		day: 'numeric',
		month: 'long'
	}).format(new Date(value));
}

function formatTimeLabel(value) {
	if (!value) {
		return '';
	}

	return new Intl.DateTimeFormat('cs-CZ', {
		hour: '2-digit',
		minute: '2-digit'
	}).format(new Date(value));
}

function formatDurationLabel(minutesValue) {
	const minutes = Math.max(parseInt(minutesValue, 10) || 0, 0);

	if (minutes === 0) {
		return '0 min';
	}

	if (minutes % 60 === 0) {
		return `${minutes / 60} h`;
	}

	const hours = Math.floor(minutes / 60);
	const restMinutes = minutes % 60;

	if (hours === 0) {
		return `${restMinutes} min`;
	}

	return `${hours} h ${restMinutes} min`;
}

function getStatusMeta(item) {
	if (parseInt(item.IS_ONGOING, 10) === 1) {
		return { label: 'Probíhá', className: 'is-ongoing' };
	}

	if (parseInt(item.IS_PAST, 10) === 1) {
		return { label: 'Proběhlo', className: 'is-past' };
	}

	return null;
}

function getTypeMeta(item) {
	if (item.TYPE === 'group') {
		return { label: 'Skupinová', className: 'is-group' };
	}

	return { label: 'Individuální', className: 'is-individual' };
}

function getAttendees(item) {
	if (Array.isArray(item.ATTENDEES)) {
		return Array.isArray(item.ATTENDEES) ? item.ATTENDEES : [];
	}

	return (Array.isArray(item.ATTENDEE_LABELS) ? item.ATTENDEE_LABELS : []).map((label) => ({
		label,
		usrId: 0,
		addPerson: 0,
	}));
}

function getCompactAttendeeSummaryLabel(countValue) {
	const count = Math.max(parseInt(countValue, 10) || 0, 0);
	return `${count} přihl.`;
}

export default function InstructorHome({usrInfo, timerCnt}) {
	const [loading, setLoading] = useState(false);
	const [items, setItems] = useState([]);
	const [error, setError] = useState('');

	const handleExercise = (usrId) => {
		window.location.pathname = `/exercise/${usrId}`;
	};

	const loadData = () => {
		setLoading(true);
		setError('');

		post('getInstructorHomeFeed', {})
			.then((res) => {
				setItems(Array.isArray(res.data) ? res.data : []);
				setLoading(false);
			})
			.catch((err) => {
				console.error(err);
				setError('Lekce se nepodařilo načíst.');
				setLoading(false);
			});
	};

	useEffect(() => {
		loadData();
	}, []);

	const summary = useMemo(() => {
		let individualCount = 0;
		let groupCount = 0;

		items.forEach((item) => {
			if (item.TYPE === 'group') {
				groupCount += 1;
			} else {
				individualCount += 1;
			}
		});

		return {
			total: items.length,
			individualCount,
			groupCount,
		};
	}, [items]);

	return (
		<div className="page_main instructor-home-page-main">
			<AppHeader usrInfo={usrInfo} timerCnt={timerCnt} loading={loading} />
			<div className={`page instructor-home-page${usrInfo.sysAdmin === 1 || usrInfo.manager === 1 || usrInfo.instructor === 1 ? ' more' : ''}`}>
				<section className="instructor-home-page__hero">
					<div className="instructor-home-page__hero-copy">
						{/*<div className="instructor-home-page__eyebrow">Instructor Home</div>*/}
						<h1 className="instructor-home-page__title">Naplánované lekce</h1>
					</div>
					<div className="instructor-home-page__hero-actions">
						<div className="icon_refresh_main instructor-home-page__refresh" onClick={() => loadData()}>
							<img className="icon_refresh fl_l cr_p mt_01" alt="" />
						</div>
					</div>
				</section>

				<section className="instructor-home-page__summary-grid" aria-label="Souhrn lekcí">
					<div className="instructor-home-page__summary-card">
						<div className="instructor-home-page__summary-label">Celkem</div>
						<div className="instructor-home-page__summary-value">{summary.total}</div>
					</div>
					<div className="instructor-home-page__summary-card instructor-home-page__summary-card--accent">
						<div className="instructor-home-page__summary-label">Individuální</div>
						<div className="instructor-home-page__summary-value">{summary.individualCount}</div>
					</div>
					<div className="instructor-home-page__summary-card instructor-home-page__summary-card--muted">
						<div className="instructor-home-page__summary-label">Skupinové</div>
						<div className="instructor-home-page__summary-value">{summary.groupCount}</div>
					</div>
				</section>

				{error !== '' ? <div className="instructor-home-page__feedback instructor-home-page__feedback--error">{error}</div> : null}

				<section className="instructor-home-page__timeline" aria-label="Seznam lekcí">
					{items.length > 0 ? items.map((item) => {
						const statusMeta = getStatusMeta(item);
						const typeMeta = getTypeMeta(item);
						const attendees = getAttendees(item);
						const addPersonCount = Math.max(parseInt(item.ADD_PERSON, 10) || 0, 0);
						const totalAttendeeCount = item.TYPE === 'group' ? attendees.length + addPersonCount : attendees.length;
						const attendeeSummaryLabel = getCompactAttendeeSummaryLabel(totalAttendeeCount);
						const hasAttendees = attendees.length > 0;
						const note = String(item.NOTE || '').trim();
						const showExerciseLinks = item.TYPE === 'individual';

						return (
							<article key={`${item.TYPE}_${item.ID}`} className={`instructor-home-card${statusMeta ? ` ${statusMeta.className}` : ''} ${typeMeta.className}`}>
								<div className="instructor-home-card__rail" />
								<div className="instructor-home-card__main">
									<div className="instructor-home-card__topline">
										<div className="instructor-home-card__day">{formatDayLabel(item.START_AT_ISO)}</div>
										<div className="instructor-home-card__badges">
											{parseInt(item.IS_TODAY, 10) === 1 ? <span className="instructor-home-card__badge instructor-home-card__badge--today">Dnes</span> : null}
											<span className={`instructor-home-card__badge instructor-home-card__badge--type ${typeMeta.className}`}>{typeMeta.label}</span>
											{statusMeta ? <span className={`instructor-home-card__badge instructor-home-card__badge--status ${statusMeta.className}`}>{statusMeta.label}</span> : null}
										</div>
									</div>

									<div className="instructor-home-card__schedule">
										<div className="instructor-home-card__time">{formatTimeLabel(item.START_AT_ISO)} - {formatTimeLabel(item.END_AT_ISO)}</div>
										<div className="instructor-home-card__duration">{formatDurationLabel(item.LNG)}</div>
										<div className="instructor-home-card__attendance-summary">({attendeeSummaryLabel})</div>
									</div>

									{hasAttendees ? (
										<div className="instructor-home-card__attendees">
											{attendees.map((attendee, index) => (
												<span className={`instructor-home-card__attendee${attendee.label === 'Anonym' || attendee.label === '?' ? ' is-anonym' : ''}`} key={`${item.TYPE}_${item.ID}_attendee_${index}`}>
													<span className="instructor-home-card__attendee-label">{attendee.label}</span>
													{attendee.addPerson > 0 ? <span className="instructor-home-card__attendee-extra">+{attendee.addPerson}</span> : null}
													{showExerciseLinks && attendee.usrId > 0 ? (
														<button
															type="button"
															className="instructor-home-card__attendee-action"
															onClick={() => handleExercise(attendee.usrId)}
															aria-label={`Výkony klienta ${attendee.label}`}
														>
															<img className="icon2 exercise" alt="" />
														</button>
													) : null}
												</span>
											))}
										</div>
									) : null}

									{note !== '' ? <div className="instructor-home-card__note">{note}</div> : null}
								</div>
							</article>
						);
					}) : (
						<div className="instructor-home-page__empty">Zatím tu nejsou žádné dnešní ani budoucí lekce.</div>
					)}
				</section>
			</div>
		</div>
	);
}
